'use client';
import { ElementType } from 'react';

import { Options, documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, Document, MARKS } from '@contentful/rich-text-types';

import { RelatedProducts } from '@/modules/blog/components/RelatedProducts';
import { AlgoliaProvider } from '@/modules/product/components/AlgoliaSearch/AlgoliaProvider';

import { cn } from '../lib/utils';
import { CtfImage, CtfImageProps } from './CtfImage';

export type EmbeddedEntryType = CtfImageProps & {
  __typename?: string;
  sys: {
    id: string;
  };
};

export type ElementClassNames = {
  paragraph?: string;
  heading1?: string;
  heading2?: string;
  heading3?: string;
  heading4?: string;
  heading5?: string;
  ul?: string;
  ol?: string;
  bold?: string;
};

export interface ContentfulRichTextInterface {
  json: Document;
  links?: {
    entries?: {
      block?: Array<EmbeddedEntryType | null>;
      [key: string]: unknown;
    };
    assets?: {
      block?: Array<EmbeddedEntryType | null>;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
  as?: ElementType;
  className?: string;
  elementClassNames?: ElementClassNames;
}

export const EmbeddedEntry = (entry: EmbeddedEntryType) => {
  switch (entry?.__typename) {
    case 'ContentfullAsset':
      return <CtfImage {...entry} />;
    default:
      return null;
  }
};

export const contentfulBaseRichTextOptions = ({
  links,
  elementClassNames,
}: ContentfulRichTextInterface): Options => ({
  renderText: (text) => {
    return text.split(/(\[(?:m|d)?br\])/).map((segment, i) => {
      if (segment === '[br]') {
        return <br key={i} />;
      }
      if (segment === '[mbr]') {
        return <br key={i} className="lg:hidden" />;
      }
      if (segment === '[dbr]') {
        return <br key={i} className="hidden lg:block" />;
      }
      return segment;
    });
  },
  renderMark: {
    [MARKS.BOLD]: (text) => (
      <span className={cn('font-bold', elementClassNames?.bold)}>{text}</span>
    ),
  },
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => {
      if (!node?.content) {
        return null;
      }

      return <p className={cn('min-h-6', elementClassNames?.paragraph)}>{children}</p>;
    },
    [BLOCKS.HEADING_1]: (node, children) => {
      if (!node?.content) {
        return null;
      }

      return (
        <h1 className={cn('px-0 text-3xl font-bold lg:text-6xl', elementClassNames?.heading1)}>
          {children}
        </h1>
      );
    },
    [BLOCKS.HEADING_2]: (node, children) => {
      if (!node?.content) {
        return null;
      }

      return (
        <h2 className={cn('px-0 text-2xl font-bold lg:text-4xl', elementClassNames?.heading2)}>
          {children}
        </h2>
      );
    },
    [BLOCKS.HEADING_3]: (node, children) => {
      if (!node?.content) {
        return null;
      }

      return (
        <h3 className={cn('text-xl font-bold lg:text-3xl', elementClassNames?.heading3)}>
          {children}
        </h3>
      );
    },
    [BLOCKS.HEADING_4]: (node, children) => {
      if (!node?.content) {
        return null;
      }

      return (
        <h4 className={cn('text-lg font-bold lg:text-2xl', elementClassNames?.heading4)}>
          {children}
        </h4>
      );
    },
    [BLOCKS.HEADING_5]: (node, children) => {
      if (!node?.content) {
        return null;
      }

      return (
        <h5 className={cn('text-lg font-bold lg:text-xl', elementClassNames?.heading5)}>
          {children}
        </h5>
      );
    },
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const entry = links?.assets?.block?.find((item) => item?.sys?.id === node.data.target.sys.id);

      if (!entry) {
        return null;
      }

      return <EmbeddedEntry {...entry} />;
    },
    [BLOCKS.UL_LIST]: (node, children) => {
      if (!node?.content) {
        return null;
      }

      return <ul className={cn('list-disc pl-6', elementClassNames?.ul)}>{children}</ul>;
    },
    [BLOCKS.OL_LIST]: (node, children) => {
      if (!node?.content) {
        return null;
      }

      return <ol className={cn('list-decimal pl-6', elementClassNames?.ol)}>{children}</ol>;
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      return (
        <AlgoliaProvider>
          <RelatedProducts entryId={node.data.target.sys.id} />
        </AlgoliaProvider>
      );
    },
    [BLOCKS.HR]: () => <hr className="my-5 border-t border-outline-lighter lg:my-8" />,
    [BLOCKS.QUOTE]: (_node, children) => (
      <blockquote className="border-l-0 pl-0 font-normal not-italic text-gray-500">
        {children}
      </blockquote>
    ),
  },
});

export const CtfRichText = ({
  json,
  links,
  className,
  as = 'article',
  elementClassNames,
  ...props
}: ContentfulRichTextInterface) => {
  const baseOptions = contentfulBaseRichTextOptions({ links, json, elementClassNames });

  const Component = as;

  return (
    <Component className={cn('prose prose-sm rtf-content py-4 lg:py-8', className)} {...props}>
      {documentToReactComponents(json, baseOptions)}
    </Component>
  );
};
